import { type ReactNode, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { RouteSuspenseFallback } from "routes/route-suspense-fallback.component";

import { useAppSelector } from "app/hooks";
import { selectLDIdentified } from "features/auth/auth-slice";
import useRouteGateReady from "hooks/use-route-gate-ready.hook";

import {
  hasClassicHandoffState,
  toClassicPath,
  toV3Path
} from "./scheduler-paths";
import { useSchedulerChoice } from "./use-scheduler-choice.hook";

interface Props {
  /** Which scheduler this route renders. */
  target: "classic" | "v3";
  children: ReactNode;
}

/**
 * ENG-1981: renders its scheduler, or sends the visit to the other one, by the person's
 * choice. It holds a fallback until the flags, the user and the choice are known — a
 * redirect decided on missing data would rewrite the URL for good (ENG-1668).
 *
 * The decision is taken once per visit (`location.key`): a choice or flag that changes
 * mid-visit applies on the next navigation, so it can never unmount an open form.
 *
 * Development access (`scheduling-v-3-dev-access`) lets `/scheduling-v3` open whatever the
 * choice, so its menu entry always works; everything else follows the choice as for everyone.
 * It is latched with the rest of the decision, so turning it off mid-visit never unmounts a form.
 *
 * DEV-3609: "the flags are known" means LaunchDarkly has answered for THIS person. Until
 * `identify()` resolves, the flags are the anonymous bootstrap set (`src/index.tsx`), and
 * `scheduling-v-3-feature` targets practices, which the anonymous context has none of — so a
 * reload of `/scheduling-v3` read the flag as off, latched "classic" and sent the person there
 * until their next navigation. Waiting on `ldIdentified` shares the gate's one deadline, so a
 * LaunchDarkly that never identifies still opens a scheduler after the grace period.
 */
export function SchedulerRouteGate({ target, children }: Props) {
  const location = useLocation();
  const choice = useSchedulerChoice();
  const ldIdentified = useAppSelector(selectLDIdentified);
  const gateReady = useRouteGateReady(choice.userLoaded && ldIdentified);
  const decision = useRef<{
    key: string;
    redirectTo: string | null;
    direct: boolean;
  } | null>(null);

  if (!gateReady) return <RouteSuspenseFallback />;

  if (decision.current?.key !== location.key) {
    // V3 with development access opens directly, so it need not wait on the choice.
    const direct = target === "v3" && choice.devAccess;
    if (!direct && choice.status === "pending")
      return <RouteSuspenseFallback />;
    const { pathname, search, state } = location;
    let redirectTo: string | null = null;
    if (target === "v3" && !choice.useV3 && !direct) {
      redirectTo = toClassicPath(pathname, search);
    } else if (
      target === "classic" &&
      choice.useV3 &&
      !hasClassicHandoffState(state)
    ) {
      redirectTo = toV3Path(pathname, search);
    }
    decision.current = { key: location.key, redirectTo, direct };
  }

  if (!decision.current.direct && choice.status === "pending")
    return <RouteSuspenseFallback />;
  const { redirectTo } = decision.current;
  return redirectTo ? <Navigate to={redirectTo} replace /> : <>{children}</>;
}
